import React, { Dispatch, SetStateAction } from "react"
import { useAppDispatch } from "lib/store"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import { User, UserStatus } from "lib/types/generated/graphql-types"
import dayjs from "dayjs"

import { Container, Date, Email, Name, StyledTableRow, Username, Wrapper } from "./UserTable.styled"
import ErrorMessage from "components/Ui/Messages/ErrorMessage"
import Status from "components/Ui/Status"

import { ReactComponent as UserIcon } from "images/icons/user-large.svg"
import { ReactComponent as ExternalIcon } from "images/icons/id-card.svg"
import TableHeader from "../Ui/Table/TableHeader"
import { useSelector } from "react-redux"
import { selectSidebarCollapsed } from "lib/store/services/ui/selectors"
import { ReactComponent as UserSlash } from "images/icons/user-slash.svg"
import TableRow from "../Ui/Table/TableRow"

type Props = {
  users: User[]
  loading?: boolean
  error: object | undefined
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
  setUserEmail: Dispatch<SetStateAction<string>>
}

export const UserTable = ({ users, loading = false, error, setEditId, setUserEmail }: Props) => {
  const dispatch = useAppDispatch()

  const sideBarCollapsed = useSelector(selectSidebarCollapsed)

  const getStatus = (status: UserStatus) => {
    if (status === UserStatus.Confirmed) return "Confirmed"
    if (status === UserStatus.Archived) return "Archived"
    if (status === UserStatus.ForceChangePassword) return "Change password"
    if (status === UserStatus.Unconfirmed) return "Unconfirmed"
    if (status === UserStatus.ExternalProvider) return "External"
    if (status === UserStatus.Unknown) return "Unknown"
    return status
  }

  return (
    <Wrapper className={loading ? "blur" : ""}>
      <Container>
        {error && (
          <ErrorMessage
            showRefreshButton
            message={
              <>
                Error loading Users. Try refreshing the page, or contact{" "}
                <a href="mailto:support@brinkcommerce.com">support</a>.
              </>
            }
          />
        )}

        <TableHeader iconHeader={true}>
          <Username>User</Username>
          <div className="hideOnSmallScreens">Status</div>
          <div className="hideOnMediumScreens">Confirmation status</div>
          <Date className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
            Created
          </Date>
          <Date className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
            Updated
          </Date>
        </TableHeader>
        {users.length === 0 && (
          <TableRow>
            <UserSlash />
            No users in this group
          </TableRow>
        )}
        {[...users].map((user) => (
          <StyledTableRow
            key={user.username}
            handleOnClick={() => {
              setEditId(user.username)
              setUserEmail(user.email)
              dispatch(showEditSidebar())
            }}
          >
            {user.status === UserStatus.ExternalProvider ? <ExternalIcon /> : <UserIcon />}
            <Username>
              <div>
                <Name>
                  {user.givenName} {user.familyName}
                </Name>
                <Email>{user.email}</Email>
              </div>
            </Username>
            <div className="hideOnSmallScreens">
              <Status status={user.enabled ? "Enabled" : "Disabled"} />
            </div>
            <div className="hideOnMediumScreens">
              <Status status={getStatus(user.status)} />
            </div>
            <Date className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
              {dayjs(user.created).format("YYYY-MM-DD")}
            </Date>
            <Date className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
              {dayjs(user.updated).format("YYYY-MM-DD")}
            </Date>
          </StyledTableRow>
        ))}
      </Container>
    </Wrapper>
  )
}
